// The purchasing plan for bought sell items, shared by the dashboard (worked out in the browser on every
// change), the server and the MCP (reorder_suggestions), so they always agree.
//
// The reorder rules HHL used before HHL Ops, kept as they were:
// - Rate: half the last 90 days' average, half the last 12 months' (an item first sold less than a year
//   ago uses its own age). No stock and no sales in 90 days = stopped selling (rate 0).
// - Safety stock: z × daily variability × √(lead time + review period), z from the item's in-stock target.
//   Variability is the SD of weekly sales ÷ √7 (the old planning used daily sales; browsers have weekly totals, and
//   for independent days it's the same). Under 4 weeks of history it's √rate (Poisson).
// - Classes by units sold: A = the best sellers making 80% of units, B = the next 15%, C = the rest; in-stock
//   targets 98 / 95 / 90 % (planning_settings). An item's own target or safety stock wins (item_plan).
// Demand is the forecast (shared/forecast.ts: stockout-adjusted, seasonal, trend, overrides and scoped
// adjustments) when `forecasts` is given, else the blended rate with Jeff's monthly overrides
// (forecast_override); wholesale/B2B demand (b2b_demand) is added on its due date; open PO lines (drafts
// too) arrive on their promised date, else the PO's expected arrival, else its ship date, else created +
// lead time. Lead time, MOQ and order cycle come from the vendor link, else the vendor's defaults, else
// the planning settings. Stock policy per item: the forecast with safety stock, min/max (below min, top up
// to max) or a target level (top up to it).
// Stock (available at HHL) is projected day by day: forecast sales are lost when out of stock, B2B waits.
// - need date: the first day stock falls below safety stock (while a PO for it is on its way, the first day it runs
//   out: the dip under the floor is what that PO is for)
// - order by: need date − lead time; "Order now" when that's within the review period
// - qty: enough to stay above safety stock from the day it lands to `cover_days` after, then the vendor's
//   MOQ and order multiple (shared/po.ts), plus a hint when a bit more reaches a cheaper price break
// - cover through (migration 061, a person's: shared/cover.ts): a bought item that would fall below safety stock
//   before that day is due now, and its quantity lasts at least to then
// Missing lead times fall back to the default and anything guessed is flagged; nothing fails silently.
// Make planning --- With `boms` (MRP, shared/mrp.ts): items a CM builds are planned as builds (the make vendor's
// lead time, its MOQ and price), level by level; each build's parts get its demand on the day it starts (open make
// POs' too), so parts sold and used add both, and every level is offset by the lead times above it. Parts in a BOM
// are planned like bought items, netted against stock at every location (HHL and the CMs) and open POs, and ship
// to the CM that uses them. Each item in a BOM gets its planned orders for a year with what each is for (pegging).
// Vendor closures --- days a vendor doesn't work (Chinese New Year) that fall inside an order's lead time are added to
// it (shared/closures.ts): order-by moves earlier, and an order whose lead time misses them is unchanged.
import type { BomIndex } from "./boms.ts";
import { type Closure, type ClosureDelay, type ClosureRow, closureIndex, delayFor, delayText, landsOn, startBy } from "./closures.ts";
import { componentsOf, isConsigned } from "./consignment.ts";
import type {
  B2bDemand,
  ForecastOverride,
  Item,
  ItemPlan,
  Location,
  MakeOrder,
  PlanningSettings,
  Po,
  PoLine,
  SalesSummary,
  StockBalance,
  StockLevel,
  Vendor,
  VendorItem,
} from "./db.gen.ts";
import type { Forecasts, ItemForecast } from "./forecast.ts";
import {
  type BomGraph,
  bomGraph,
  type ChainStep,
  chainOf,
  type DependentDemand,
  type MakeSpec,
  type Peg,
  type PegDemand,
  type PegRoot,
  peg,
  planLevels,
  rootsOf,
  weeklyDemand,
} from "./mrp.ts";
import { isOpen, mulRound, type PriceBreak, priceFor, validQty } from "./po.ts";

export type PlanSettings = Pick<
  PlanningSettings,
  "review_days" | "default_lead_time_days" | "cover_days" | "horizon_days" | "overstock_months" | "service_a" | "service_b" | "service_c"
>;
export const DEFAULT_PLAN_SETTINGS: PlanSettings = {
  review_days: 7,
  default_lead_time_days: 30,
  cover_days: 60,
  horizon_days: 90,
  overstock_months: 12,
  service_a: 98,
  service_b: 95,
  service_c: 90,
};

export type PlanItem = Pick<
  Item,
  | "id"
  | "kind"
  | "source"
  | "sku"
  | "name"
  | "photo_url"
  | "procurement"
  | "lifecycle"
  | "archived"
  | "shopify_status"
  | "shopify_tracked"
  | "shopify_product_title"
  | "shopify_variant_title"
> &
  Partial<Pick<Item, "shopify_product_id">>;
export type PlanVendor = Pick<Vendor, "id" | "name" | "short_name" | "currency" | "archived"> &
  Partial<Pick<Vendor, "lead_time_days" | "moq" | "order_cycle_days" | "kind" | "default_ship_to_location_id" | "country">>;
export type PlanLink = Pick<
  VendorItem,
  | "id"
  | "vendor_id"
  | "item_id"
  | "vendor_sku"
  | "vendor_name"
  | "unit_price"
  | "currency"
  | "price_breaks"
  | "moq"
  | "order_multiple"
  | "lead_time_days"
  | "is_preferred"
  | "created_at"
>;

export type PlanData = {
  /** YYYY-MM-DD in HHL's time zone (planToday()). */
  today: string;
  settings: PlanSettings | undefined;
  items: readonly PlanItem[];
  sales: readonly Pick<SalesSummary, "id" | "as_of" | "units_30" | "units_90" | "units_365" | "weekly" | "first_sold" | "last_sold">[];
  /** `committed` (units on customer orders not shipped yet) only explains the gap between the two; 0 when left out. */
  stock: readonly (Pick<StockLevel, "item_id" | "location_id" | "on_hand" | "available"> & { committed?: number })[];
  locations: readonly (Pick<Location, "id" | "kind" | "active" | "ignored"> & Partial<Pick<Location, "name" | "vendor_id" | "is_cm">>)[];
  vendors: readonly PlanVendor[];
  vendorItems: readonly PlanLink[];
  pos: readonly (Pick<Po, "id" | "number" | "vendor_id" | "status" | "expected_ship" | "expected_arrive" | "created_at"> &
    Partial<Pick<Po, "kind" | "ship_to_location_id" | "hhl_share_closed">>)[];
  /** Pre-sales --- qty_direct: units of a make PO line that go straight from the CM to backers, never to HHL. */
  poLines: readonly (Pick<PoLine, "id" | "po_id" | "item_id" | "qty" | "qty_received" | "promised_date"> & Partial<Pick<PoLine, "qty_direct">>)[];
  itemPlans: readonly (Pick<ItemPlan, "id" | "policy" | "safety_stock" | "service_level" | "snooze_until" | "snooze_reason"> &
    Partial<Pick<ItemPlan, "stock_policy" | "min_qty" | "max_qty" | "target_qty" | "hidden_reason">>)[];
  overrides: readonly Pick<ForecastOverride, "item_id" | "month" | "units">[];
  b2b: readonly Pick<B2bDemand, "id" | "item_id" | "qty" | "due_date" | "note" | "done">[];
  /** The forecast (buildForecasts()); without it, demand is the blended sales rate. */
  forecasts?: Forecasts;
  // Make planning ---
  /** BOMs (indexBoms()): with them, make items are planned as builds and their parts get the builds' demand (MRP). */
  boms?: BomIndex;
  /** The stock ledger (local items, and anything at a CM), counted at every location in use. */
  balances?: readonly Pick<StockBalance, "item_id" | "location_id" | "qty">[];
  /** Make orders (consignment): an open make PO line's units still to build and the BOM snapshot it builds from. */
  makeOrders?: readonly Pick<MakeOrder, "id" | "po_line_id" | "item_id" | "cm_location_id" | "qty_output" | "components">[];
  /** Pre-sales --- units of runs that aren't for sale (shared/presales.ts planReservations()): demand on their day. */
  reservations?: readonly PlanReservation[];
  /** Pre-sales --- Shopify products a pre-sale sells (presaleProducts()): planned though their title says Kickstarter. */
  presaleProducts?: readonly string[];
  /** Vendor closures --- by country (vendor.country) or vendor; their days inside an order's lead time are added to it. */
  closures?: readonly ClosureRow[];
  /** Cover through --- the person's (or a draft's) day to buy to, for bought items. */
  coverTo?: CoverTo;
};

/**
 * Cover through (migration 061; Mitch, 2026-11-03: "ensure we had no stockouts through December 31st"): how far ahead
 * a person is buying, as days from today, for every vendor (`all`) and per vendor (which wins). Bought items only: builds
 * follow the Make plan.
 */
export type CoverTo = { all: number | null; byVendor: ReadonlyMap<string, number> };

/**
 * Pre-sales --- units of a run that planning never counts as for sale: backers' units (a pre-sale shipping from the CM),
 * a pre-sale shipping from HHL, or a hold (gifts, influencers). `qty` is the demand to plan on `day` (ISO), at `site`
 * (a CM's location: netted against the stock there first, like a build's parts) or at HHL (null), waiting for stock
 * like B2B; the rest is what it is ("924 reserved, all of it on PO-1018's lines"), for explain().
 */
export type PlanReservation = {
  itemId: string;
  kind: "backers" | "presale" | "held";
  /** The pre-sale's name, or the hold's reason. */
  label: string;
  runId: string;
  runNumber: string;
  reserved: number;
  shipped: number;
  /** Of it, on the run's make PO lines as going straight to backers (already out of the PO's supply). */
  direct: number;
  qty: number;
  day: string;
  site: string | null;
  why: string;
};

export const BUCKETS = ["order_now", "at_risk", "this_month", "upcoming", "overstock", "ok", "snoozed", "not_planned"] as const;
export type Bucket = (typeof BUCKETS)[number];
export const BUCKET_LABELS: Record<Bucket, string> = {
  order_now: "Order now",
  at_risk: "At risk",
  this_month: "This month",
  upcoming: "Upcoming",
  overstock: "Overstock / slow",
  ok: "OK",
  snoozed: "Snoozed",
  not_planned: "Not planned",
};
export type Policy = ItemPlan["policy"];
export type StockPolicy = ItemPlan["stock_policy"];
export type ItemClass = "A" | "B" | "C";

export type FlagCode =
  | "no_vendor"
  | "no_preferred"
  | "vendor_archived"
  | "default_lead_time"
  | "no_moq"
  | "no_price"
  | "no_sales"
  | "new_item"
  | "stopped_selling"
  | "po_late"
  | "po_date_guessed"
  | "oversold"
  // Make planning ---
  | "bom_draft"
  | "no_cm_site"
  | "ship_split";
export type Flag = { code: FlagCode; message: string };

export type Incoming = {
  poId: string;
  poNumber: string;
  lineId: string;
  qty: number;
  /** When it's expected at HHL (today for a late one). */
  arrive: string;
  dateFrom: "promised" | "po_arrive" | "po_ship" | "lead_time";
  draft: boolean;
  /** Its date has passed and it hasn't all arrived. */
  late: boolean;
  /** Make planning --- The PO's kind (a make PO is a build) and where it ships. */
  kind?: Po["kind"];
  shipTo?: string | null;
  /** Pre-sales --- units of the line that go straight from the CM to backers (not in `qty`). */
  direct?: number;
};

export type MonthForecast = { month: string; units: number; override: boolean; b2b: number };

export type PriceHint = { qty: number; extra: number; unitPrice: number; currentUnitPrice: number; saves: number; extraCoverDays: number };

export type ItemPlanResult = {
  item: PlanItem;
  vendor: PlanVendor | null;
  link: PlanLink | null;
  /** The link with the MOQ that applies (the vendor's default when the link has none), for quantities. */
  terms: PlanLink | null;
  policy: Policy;
  planned: boolean;
  /** Why it isn't planned (planned = false). */
  skip: string;
  cls: ItemClass | null;
  // Demand
  /** Forecast units per day: the next 90 days' average (the blended sales rate without a forecast). */
  rate: number;
  rate90: number;
  rate365: number;
  /** Units sold per day, standard deviation. */
  sd: number;
  historyDays: number;
  /** The item's forecast (null when the plan ran without one). */
  forecast: ItemForecast | null;
  /** Forecast units on day `d` of the projection (overrides and adjustments included, B2B not). */
  demandOn: (d: number) => number;
  /** Forecast for the next 30 days (overrides included, B2B not). */
  next30: number;
  months: MonthForecast[];
  b2bOpen: number;
  /** Open B2B demand by projection day (overdue = day 0). */
  b2bDue: { day: number; qty: number }[];
  // Stock policy, safety stock and terms
  stockPolicy: StockPolicy;
  /** min_max: below min, top up to max; target: top up to the target (min = max = target). */
  minQty: number | null;
  maxQty: number | null;
  serviceLevel: number;
  serviceFrom: "class" | "item";
  z: number;
  safetyStock: number;
  safetyFrom: "computed" | "item" | "policy";
  leadTime: number;
  leadTimeFrom: "link" | "vendor" | "default";
  leadTimeDefault: boolean;
  /** Vendor closures --- its vendor's (the CM's for a build), as days from today; [] for none. */
  closures: Closure[];
  /** When an order placed on day `start` lands: the lead time, plus the days its vendor is closed inside it. */
  landsOn: (start: number) => number;
  /** The latest day to place an order that lands by day `need` (need − lead time, earlier across a closure). */
  startBy: (need: number) => number;
  moq: number;
  moqFrom: "link" | "vendor" | null;
  multiple: number;
  /** Days of stock an order buys after it lands: the vendor's order cycle, else the settings. */
  coverFor: number;
  coverFrom: "vendor" | "settings";
  /**
   * Cover through --- the day (from today) it's bought to last to, whose (the vendor's own or the one for every vendor),
   * and whether it falls short before then (so it's due now and the quantity lasts to then); null without one.
   */
  coverTo: { day: number; from: "vendor" | "all"; short: boolean } | null;
  // Supply
  onHand: number;
  available: number;
  /** Shopify's committed at HHL: on hand but on customer orders (shared/stock.ts stockNote() explains the gap). */
  committed: number;
  incoming: Incoming[];
  /** Make planning --- open make PO lines with building left (Pre-sales: a line all for backers too, with qty 0). */
  builds: Incoming[];
  incomingQty: number;
  // Projection (days from today)
  /** First day projected stock runs out; null = not within the projection. */
  stockoutDay: number | null;
  /** Days of cover: stockoutDay, else extrapolated at the forecast rate; null = no forecast sales. */
  coverDays: number | null;
  /** First day projected stock falls below safety stock. */
  needDay: number | null;
  orderByDay: number | null;
  /** Projection length in days (cover beyond this shows as "more than"). */
  horizon: number;
  // Suggestion
  /** When the suggested order lands (placed on the order-by day, today if that's passed); null without one. */
  arriveDay: number | null;
  /** Vendor closures --- how much earlier closures make the suggested order's order-by (or, late, its landing later); null when they don't. */
  delay: ClosureDelay | null;
  rawQty: number;
  qty: number;
  unitPrice: number | null;
  currency: string;
  cost: number | null;
  priceHint: PriceHint | null;
  /** Late PO lines, or stock running out before a PO lands. */
  atRisk: boolean;
  /** Why it's at risk, in words. */
  riskNote: string;
  overstock: boolean;
  bucket: Bucket;
  flags: Flag[];
  snoozedUntil: string | null;
  snoozeReason: string;
  // Make planning ---
  /** make: built at a CM (its suggestion is a build: a make PO); buy: bought from its vendor. */
  role: "buy" | "make";
  /** For make items: the CM, the BOM and what one unit takes. */
  make: MakeSpec | null;
  /** Low-level code (MRP planning order); -1 for items in no BOM. */
  level: number;
  /** Demand from builds that use it (planned ones, and open make POs), on the day each build starts. */
  dependent: DependentDemand[];
  /** Each of those after stock already at the CM it ships to (same order as `dependent`). */
  depNet: number[];
  /** All of it, and what's left after stock at the CMs per day over the projection. */
  dependentQty: number;
  depRate: number;
  /** Stock at CM sites (Shopify's and the ledger): it only covers builds at that same site, never HHL's sales. */
  atSites: number;
  /** Where its suggestion ships: HHL (or the vendor's usual place), or the CM that uses it. */
  shipTo: string | null;
  /** Items in a BOM: the orders the plan expects over the next year, with what each is for. [] for the rest. */
  orders: MrpOrder[];
  // Pre-sales ---
  /** Units of runs that aren't for sale (backers, pre-orders, holds): each with the day and what's left to plan. */
  reserved: { r: PlanReservation; day: number; net: number; fromSite: number }[];
  /** What they still take from HHL's supply (after stock already at the CM). */
  reservedQty: number;
};

/** A planned order: a purchase, or a build at a CM (a make PO), with its pegging trail. */
export type MrpOrder = {
  key: string;
  itemId: string;
  kind: "purchase" | "make";
  vendorId: string | null;
  shipTo: string | null;
  /** When the pegs go to several places: how much is for each. */
  shipSplit: { shipTo: string | null; qty: number }[];
  /** Order-by: projection day to place it (below 0 = late). For a build, the day it should start. */
  orderBy: number;
  /** The day it's placed (today at the earliest). */
  orderDay: number;
  /** When it's due at its ship-to (a build: finished at HHL or the next CM). */
  arriveDay: number;
  /** The day stock would fall below its floor without it. */
  needDay: number;
  qty: number;
  unitPrice: number | null;
  cost: number | null;
  currency: string;
  /** The order the plan suggests now (the rest are expected to follow). */
  first: boolean;
  leadTime: number;
  /** Vendor closures --- how much earlier its vendor's closures make its order-by (or, late, its landing later); null when they don't. */
  delay: ClosureDelay | null;
  /** Its own lead time (closures included) plus every build's above it, up to the sell item. */
  cumLead: number;
  chain: ChainStep[];
  pegs: Peg[];
  roots: PegRoot[];
  priceHint: PriceHint | null;
};

export type Plan = {
  today: string;
  settings: PlanSettings;
  results: ItemPlanResult[];
  byItem: Map<string, ItemPlanResult>;
  /** False until the first sales pull: every rate is 0. */
  hasSales: boolean;
  /** Make planning --- With BOMs: the graph, each CM's location, HHL's default location and location names. */
  mrp: { graph: BomGraph; siteOf: Map<string, string | null>; hhl: string | null; names: Map<string, string> } | null;
};

// Dates ----------------------------------------------------------------------------------------------
// Whole days as numbers (days since 1970-01-01 UTC), so the projection never meets time zones or DST.

import { PLAN_TIME_ZONE, planToday } from "./day.ts";

export { PLAN_TIME_ZONE, planToday };

const DAY_MS = 86_400_000;
const MONTH_DAYS = 30.44;
export const dayNum = (iso: string) => Math.round(Date.parse(`${iso.slice(0, 10)}T00:00:00Z`) / DAY_MS);
export const isoOfDay = (n: number) => new Date(n * DAY_MS).toISOString().slice(0, 10);
export const addPlanDays = (iso: string, n: number) => isoOfDay(dayNum(iso) + n);
const textOf = (d: string | Date | null | undefined) => (d == null ? null : typeof d === "string" ? d.slice(0, 10) : d.toISOString().slice(0, 10));

/** The month a day is in, as its first day (2026-11-01). */
export const monthOf = (iso: string) => `${iso.slice(0, 7)}-01`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Nov 20", or "Nov 20, 2027" outside today's year. */
export function shortDate(iso: string, today?: string): string {
  const [y, m, d] = iso.split("-").map(Number);
  return `${MONTHS[m - 1]} ${d}${today && today.slice(0, 4) !== String(y) ? `, ${y}` : ""}`;
}
/** A plan day (days after `today`) as people read it: "Today" for today or before, else "Nov 20". */
export const planDayText = (day: number | null, today: string) => (day === null ? "—" : day <= 0 ? "Today" : shortDate(addPlanDays(today, day), today));
export const monthLabel = (iso: string) => `${MONTHS[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

// Numbers --------------------------------------------------------------------------------------------

/** z for a one-sided in-stock probability (Acklam's inverse normal, |error| < 1.2e-9): 95 % → 1.645. */
export function zFor(percent: number): number {
  const p = Math.min(0.9999, Math.max(0.5, percent / 100));
  const a = [-39.69683028665376, 220.9460984245205, -275.9285104469687, 138.357751867269, -30.66479806614716, 2.506628277459239];
  const b = [-54.47609879822406, 161.5858368580409, -155.6989798598866, 66.80131188771972, -13.28068155288572];
  const c = [-0.007784894002430293, -0.3223964580411365, -2.400758277161838, -2.549732539343734, 4.374664141464968, 2.938163982698783];
  const d = [0.007784695709041462, 0.3224671290700398, 2.445134137142996, 3.754408661907416];
  if (p > 0.97575) {
    const q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) / ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
  const q = p - 0.5;
  const r = q * q;
  return ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) / (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
}

/** 1,234 / 3.5 / 0.25: whole numbers from 10 up. */
export function fmt(n: number): string {
  const abs = Math.abs(n);
  const places = abs >= 10 || Number.isInteger(n) ? 0 : abs >= 1 ? 1 : 2;
  const [whole, frac] = abs.toFixed(places).split(".");
  return `${n < 0 ? "−" : ""}${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${frac && Number(frac) ? `.${frac.replace(/0+$/, "")}` : ""}`;
}
const fmtMoney = (n: number, currency: string) =>
  `${currency === "USD" ? "$" : `${currency} `}${n.toFixed(n < 10 ? 2 : 0).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

// Demand -----------------------------------------------------------------------------------------------

type Sales = PlanData["sales"][number];

/** The blended rate (½ 90-day + ½ 12-month) and the variability safety stock uses, from an item's sales summary. */
export function salesRates(s: Sales | undefined) {
  if (!s) return { rate: 0, rate90: 0, rate365: 0, sd: 0, historyDays: 0, stopped: false };
  // Synced rows keep their identity until they change, so each one's rates are worked out once.
  const hit = ratesCache.get(s);
  if (hit) return hit;
  const out = computeRates(s);
  ratesCache.set(s, out);
  return out;
}
const ratesCache = new WeakMap<Sales, ReturnType<typeof computeRates>>();

function computeRates(s: Sales) {
  const asOf = dayNum(textOf(s.as_of) ?? "");
  const first = textOf(s.first_sold);
  const age = first ? Math.max(1, asOf - dayNum(first) + 1) : 365;
  const window = Math.min(365, age);
  const recent = Math.min(90, window);
  const rate365 = Math.max(0, s.units_365 / window);
  const rate90 = Math.max(0, s.units_90 / recent);
  const rate = 0.5 * rate90 + 0.5 * rate365;
  const weeks = (s.weekly ?? []).slice(-Math.min(52, Math.max(1, Math.ceil(window / 7))));
  let sd = Math.sqrt(rate);
  if (weeks.length >= 4) {
    const mean = weeks.reduce((n, w) => n + w, 0) / weeks.length;
    sd = Math.sqrt(weeks.reduce((n, w) => n + (w - mean) ** 2, 0) / (weeks.length - 1)) / Math.sqrt(7);
  }
  return { rate, rate90, rate365, sd, historyDays: window, stopped: false };
}

// The plan -------------------------------------------------------------------------------------------------

const PRE_ORDER = /pre-?order|kickstarter/i;
// Reused between items: the projection's per-day demand, arrivals and stock.
let buf = { fc: new Float64Array(0), b2b: new Float64Array(0), inc: new Float64Array(0), end: new Float64Array(0) };

const MONTH_TABLE_DAYS = 2000;
const monthOfOffset = (t0: number, d: number) => {
  const date = new Date((t0 + d) * DAY_MS);
  return date.getUTCFullYear() * 12 + date.getUTCMonth();
};

/**
 * What an order landing on day `arrive` needs, from the projection in `buf` (`before` = stock the day
 * before it lands): with a max (min/max, target) enough to top up to it, else enough to stay above
 * safety stock for `cover` days after it lands.
 */
function orderQty(arrive: number, before: number, H: number, cover: number, safetyStock: number, max: number | null): number {
  const { fc, b2b, inc } = buf;
  if (max !== null) return Math.max(0, Math.ceil(max - (before + inc[arrive] - b2b[arrive]) - 1e-9));
  let q = before;
  let worst = Number.POSITIVE_INFINITY;
  for (let d = arrive; d < Math.min(H, arrive + cover); d++) {
    q += inc[d] - b2b[d] - fc[d];
    if (q < worst) worst = q;
  }
  return Math.max(0, Math.ceil(safetyStock - worst - 1e-9));
}

/** Make planning --- How far ahead builds are exploded into their parts' demand (and planned orders listed). */
export const MRP_DAYS = 365;

/** A cheaper price break a little further up, unless it would buy into overstock ("+400 reaches the 1k price"). */
function priceHintFor(terms: PlanLink, qty: number, unitPrice: number | null, rate: number, coverFor: number, overstockDays: number): PriceHint | null {
  if (!qty || unitPrice == null || rate <= 0) return null;
  const breaks = ((Array.isArray(terms.price_breaks) ? terms.price_breaks : []) as PriceBreak[])
    .map((b) => ({ min_qty: Number(b.min_qty), unit_price: Number(b.unit_price) }))
    .filter((b) => b.min_qty > qty && b.unit_price < unitPrice)
    .sort((a, b) => a.min_qty - b.min_qty);
  if (!breaks[0]) return null;
  const target = validQty(terms, breaks[0].min_qty);
  const extraCoverDays = (target - qty) / rate;
  if (coverFor + extraCoverDays > overstockDays) return null;
  const price = priceFor(terms, target) ?? breaks[0].unit_price;
  return { qty: target, extra: target - qty, unitPrice: price, currentUnitPrice: unitPrice, saves: mulRound(target, unitPrice - price, 2), extraCoverDays };
}

/**
 * Plans every bought sell item (kind sell, not archived, procurement buy or both). With `reuse`, only the
 * items in `only` are planned again (their forecast or settings changed); the rest keep `prev`'s result
 * unless their class moved. The caller makes sure nothing else changed (same day, same stock…).
 * Make planning --- With `data.boms` it is also the MRP: items a CM builds (role make) and every part in their
 * BOMs are planned too, level by level (shared/mrp.ts), and get `orders` with their pegging.
 */
export function planPurchasing(data: PlanData, reuse?: { prev: Plan; only: ReadonlySet<string> }): Plan {
  const settings = { ...DEFAULT_PLAN_SETTINGS, ...(data.settings ?? {}) };
  for (const k of Object.keys(DEFAULT_PLAN_SETTINGS) as (keyof PlanSettings)[]) settings[k] = Number(settings[k]);
  const today = data.today;
  const t0 = dayNum(today);

  // Indexes ---
  const hhl = new Set(data.locations.filter((l) => l.kind === "hhl" && l.active && !l.ignored).map((l) => l.id));
  const stockOf = new Map<string, { onHand: number; available: number; committed: number }>();
  for (const s of data.stock) {
    if (!hhl.has(s.location_id)) continue;
    const cur = stockOf.get(s.item_id) ?? { onHand: 0, available: 0, committed: 0 };
    cur.onHand += Number(s.on_hand);
    cur.available += Number(s.available);
    cur.committed += Number(s.committed ?? 0);
    stockOf.set(s.item_id, cur);
  }
  // Make planning --- The ledger counts at HHL and in transit like Shopify's HHL stock. Stock at a vendor's site (a
  // CM: Shopify's levels there and the ledger) is kept apart: it only covers builds at that same site, never HHL's sales.
  const used = new Set(data.locations.filter((l) => l.active && !l.ignored).map((l) => l.id));
  const siteLocs = new Set(data.locations.filter((l) => l.kind === "vendor_site" && l.active && !l.ignored).map((l) => l.id));
  const siteStockOf = new Map<string, Map<string, number>>();
  const addSite = (item: string, loc: string, q: number) => {
    let m = siteStockOf.get(item);
    if (!m) siteStockOf.set(item, (m = new Map()));
    m.set(loc, (m.get(loc) ?? 0) + q);
  };
  for (const s of data.stock) if (siteLocs.has(s.location_id)) addSite(s.item_id, s.location_id, Number(s.on_hand));
  for (const b of data.balances ?? []) {
    if (!used.has(b.location_id)) continue;
    if (siteLocs.has(b.location_id)) {
      addSite(b.item_id, b.location_id, Number(b.qty));
      continue;
    }
    const cur = stockOf.get(b.item_id) ?? { onHand: 0, available: 0, committed: 0 };
    cur.onHand += Number(b.qty);
    cur.available += Number(b.qty);
    stockOf.set(b.item_id, cur);
  }
  const graph = data.boms ? bomGraph(data.boms) : null;
  const hhlDefault =
    data.locations.filter((l) => l.kind === "hhl" && l.active && !l.ignored).sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))[0]?.id ?? null;
  const nameOf = new Map(data.locations.map((l) => [l.id, l.name ?? ""]));
  const siteOf = new Map<string, string | null>();
  /** A CM's location, where consigned parts ship: its CM site (consignment's is_cm), else its first site, by name. */
  const cmSite = (vendorId: string) => {
    if (!siteOf.has(vendorId)) {
      const site = data.locations
        .filter((l) => l.kind === "vendor_site" && l.vendor_id === vendorId && l.active && !l.ignored)
        .sort((a, b) => Number(!!b.is_cm) - Number(!!a.is_cm) || (a.name ?? "").localeCompare(b.name ?? ""))[0];
      siteOf.set(vendorId, site?.id ?? null);
    }
    return siteOf.get(vendorId) ?? null;
  };
  const dependentOf = new Map<string, DependentDemand[]>();
  // Sub-assemblies built in the same order: their stock still free (at HHL and at the CM site first asked about), used
  // before their parts are planned (MakeSpec.builtIn).
  const insideStock = new Map<string, number>();
  const stockLeft = (itemId: string, site: string | null) => {
    if (!insideStock.has(itemId))
      insideStock.set(itemId, Math.max(0, (stockOf.get(itemId)?.available ?? 0) + (site ? (siteStockOf.get(itemId)?.get(site) ?? 0) : 0)));
    return insideStock.get(itemId) ?? 0;
  };
  const makeOrderOf = new Map((data.makeOrders ?? []).map((m) => [m.po_line_id, m]));
  const lineQtyOf = new Map(data.poLines.map((l) => [l.id, Number(l.qty)]));
  const salesOf = new Map(data.sales.map((s) => [s.id, s]));
  const vendorOf = new Map(data.vendors.map((v) => [v.id, v]));
  const linksOf = new Map<string, PlanLink[]>();
  const push = <T>(m: Map<string, T[]>, k: string, v: T) => {
    const list = m.get(k);
    if (list) list.push(v);
    else m.set(k, [v]);
  };
  for (const l of data.vendorItems) push(linksOf, l.item_id, l);
  const planOf = new Map(data.itemPlans.map((p) => [p.id, p]));
  const overridesOf = new Map<string, Map<string, number>>();
  for (const o of data.overrides) {
    const m = overridesOf.get(o.item_id) ?? new Map<string, number>();
    m.set(monthOf(textOf(o.month) ?? ""), Number(o.units));
    overridesOf.set(o.item_id, m);
  }
  const b2bOf = new Map<string, PlanData["b2b"][number][]>();
  for (const b of data.b2b) if (!b.done) push(b2bOf, b.item_id, b);
  const poOf = new Map(data.pos.map((p) => [p.id, p]));
  const linesOf = new Map<string, PlanData["poLines"][number][]>();
  // Pre-sales --- a line's units for backers never come to HHL: only the rest is supply (none once nothing else is
  // coming to HHL). A make PO line still has building to do for them, though (even one that's all for backers): it
  // stays a build, with no supply.
  const forHhl = (l: PlanData["poLines"][number]) =>
    poOf.get(l.po_id)?.hhl_share_closed ? 0 : Number(l.qty) - Number(l.qty_direct ?? 0) - Number(l.qty_received);
  const moByLine = new Map((data.makeOrders ?? []).map((m) => [m.po_line_id, m]));
  const toBuild = (l: PlanData["poLines"][number]) => {
    const mo = moByLine.get(l.id);
    return Number(l.qty_direct ?? 0) > 0 && !!mo && Number(l.qty) - Number(mo.qty_output) > 0;
  };
  for (const l of data.poLines) {
    const po = poOf.get(l.po_id);
    if (!po || !isOpen(po.status) || (forHhl(l) <= 0 && !(po.kind === "make" && toBuild(l)))) continue;
    push(linesOf, l.item_id, l);
  }
  const presaleProducts = new Set(data.presaleProducts ?? []);
  const reservationsOf = new Map<string, PlanReservation[]>();
  for (const r of data.reservations ?? []) push(reservationsOf, r.itemId, r);
  // Open make orders still to build (consignment) are edges too: a part only their BOM snapshot has (dropped from the
  // current revision) is still planned for them.
  const lineOf = new Map(data.poLines.map((l) => [l.id, l]));
  const openMakeOrders = (data.makeOrders ?? []).filter((mo) => {
    const l = lineOf.get(mo.po_line_id);
    const po = l ? poOf.get(l.po_id) : undefined;
    return !!l && !!po && isOpen(po.status) && Number(l.qty) - Number(mo.qty_output) > 0 && (forHhl(l) > 0 || toBuild(l));
  });
  const levelOf = graph
    ? planLevels(
        graph,
        openMakeOrders.map((mo) => ({ parent: mo.item_id, parts: componentsOf(mo).flatMap((c) => (isConsigned(c) ? [c.item_id] : [])) })),
      )
    : null;

  // Which items, and their classes (by rate, best first) ---
  // Make planning --- plus, with BOMs, the items CMs build and every part in their BOMs.
  const items = data.items.filter((i) => !i.archived && ((i.kind === "sell" && i.procurement !== "make") || levelOf?.has(i.id)));
  const forecastOf = (id: string) => data.forecasts?.byItem.get(id) ?? null;
  const rates = new Map(
    items.map((i) => {
      const r = salesRates(salesOf.get(i.id));
      const f = forecastOf(i.id);
      const rate = f ? f.next90 / 90 : r.rate;
      const sd = f ? f.sd : r.sd;
      // The stopped-selling rule: no stock and nothing sold in 90 days means it stopped selling. With stock history the
      // forecast knows better (a stockout isn't a lack of demand; 180+ days out is stopped).
      const stock = stockOf.get(i.id);
      const noStockNoSales = !f?.stockAdjusted && (stock?.onHand ?? 0) <= 0 && (salesOf.get(i.id)?.units_90 ?? 0) <= 0;
      if ((f ? f.level : r.rate) > 0 && noStockNoSales && f?.from !== "like") return [i.id, { ...r, sd, rate: 0, stopped: true }];
      return [i.id, { ...r, sd, rate, stopped: !!f?.stopped }];
    }),
  );
  const skipOf = (i: PlanItem): string => {
    const policy = planOf.get(i.id)?.policy ?? "manual";
    if (policy === "none") {
      // Hidden from the plan --- with why, when someone said
      const why = planOf.get(i.id)?.hidden_reason?.trim();
      return why ? `Hidden: ${why}` : "Set to not planned";
    }
    if (i.lifecycle === "eol" || i.lifecycle === "obsolete") return `${i.lifecycle === "eol" ? "End of life" : "Obsolete"}: not reordered`;
    if (i.shopify_tracked === false) return "Shopify doesn't track its stock";
    // Pre-sales --- a pre-sale (any run's, not cancelled, done ones too) says which of its product's units are the
    // campaign's, so every variant of it is planned like any item, a variant nobody reserved too.
    if (
      PRE_ORDER.test(`${i.shopify_product_title} ${i.name}`) &&
      !reservationsOf.has(i.id) &&
      !(i.shopify_product_id && presaleProducts.has(i.shopify_product_id))
    )
      return "Pre-order or Kickstarter: not planned (decision 7)";
    return "";
  };
  const skips = new Map(items.map((i) => [i.id, skipOf(i)]));
  // Make planning --- bought sell items are ranked among themselves exactly as without BOMs; make items have a pool
  // of their own (so adding BOMs never moves another item's class).
  const clsOf = new Map<string, ItemClass>();
  const bought = (i: PlanItem) => i.kind === "sell" && i.procurement !== "make";
  for (const pool of [true, false]) {
    const ranked = items
      .filter((i) => bought(i) === pool && !skips.get(i.id) && (rates.get(i.id)?.rate ?? 0) > 0)
      .map((i) => ({ id: i.id, rate: rates.get(i.id)?.rate ?? 0 }))
      .sort((a, b) => b.rate - a.rate);
    const total = ranked.reduce((n, i) => n + i.rate, 0);
    let running = 0;
    for (const i of ranked) {
      const share = total ? running / total : 0;
      running += i.rate;
      clsOf.set(i.id, share < 0.8 ? "A" : share < 0.95 ? "B" : "C");
    }
  }

  // Calendar for the longest projection: each day's month and its length. An item is projected far
  // enough for an order placed at the end of the horizon to land and last; cover beyond that is extrapolated.
  const overstockDays = Math.ceil(settings.overstock_months * MONTH_DAYS);
  let maxLead = settings.default_lead_time_days;
  let maxCover = settings.cover_days;
  for (const l of data.vendorItems) if (Number(l.lead_time_days) > maxLead) maxLead = Number(l.lead_time_days);
  for (const v of data.vendors) {
    if (Number(v.lead_time_days ?? 0) > maxLead) maxLead = Number(v.lead_time_days);
    if (Number(v.order_cycle_days ?? 0) > maxCover) maxCover = Number(v.order_cycle_days);
  }
  // Vendor closures --- each vendor's, as days from today; the days closed within a projection stretch it (only those:
  // a closure further out changes nothing).
  const closuresOf = closureIndex(data.vendors, data.closures, t0);
  // Cover through --- far enough for the furthest day anyone's buying to.
  let maxCoverTo = data.coverTo?.all ?? 0;
  for (const d of data.coverTo?.byVendor.values() ?? []) maxCoverTo = Math.max(maxCoverTo, d);
  const longest = Math.max(settings.horizon_days + maxLead + maxCover + 1, maxCoverTo + maxLead + 1);
  let maxClosed = 0;
  for (const cs of closuresOf.values()) maxClosed = Math.max(maxClosed, landsOn(cs, 0, longest) - longest);
  const maxH = Math.min(1500, Math.max(200, longest + maxClosed));
  const monthKey: string[] = new Array(maxH);
  const monthLen = new Float64Array(maxH);
  for (let d = 0; d < maxH; d++) {
    const date = new Date((t0 + d) * DAY_MS);
    monthKey[d] = `${date.toISOString().slice(0, 7)}-01`;
    monthLen[d] = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  }
  // Each day's month as a number (year × 12 + month − 1) for the forecast, far enough for the purchases
  // calendar too (a year plus lead time and cover), filled a month at a time.
  const monthNo = new Int32Array(MONTH_TABLE_DAYS);
  {
    const first = new Date(t0 * DAY_MS);
    let [y, m, day] = [first.getUTCFullYear(), first.getUTCMonth(), first.getUTCDate()];
    for (let d = 0; d < MONTH_TABLE_DAYS; ) {
      const n = Math.min(MONTH_TABLE_DAYS - d, new Date(Date.UTC(y, m + 1, 0)).getUTCDate() - day + 1);
      monthNo.fill(y * 12 + m, d, d + n);
      d += n;
      day = 1;
      [y, m] = m === 11 ? [y + 1, 0] : [y, m + 1];
    }
  }
  if (buf.fc.length < maxH) buf = { fc: new Float64Array(maxH), b2b: new Float64Array(maxH), inc: new Float64Array(maxH), end: new Float64Array(maxH) };
  const months6 = [...new Set(monthKey.slice(0, 200))].slice(0, 6).map((m) => ({ m, len: monthLen[monthKey.indexOf(m)] }));

  const planOne = (item: PlanItem): ItemPlanResult => {
    // Items in a BOM are planned again every time: their demand comes from the builds above them.
    const kept = reuse && !reuse.only.has(item.id) && !levelOf?.has(item.id) ? reuse.prev.byItem.get(item.id) : undefined;
    if (kept && kept.cls === (clsOf.get(item.id) ?? null) && kept.item === item) return kept;
    const spec = graph?.make.get(item.id) ?? null;
    const sell = item.kind === "sell";
    const ip = planOf.get(item.id);
    const r = rates.get(item.id) ?? salesRates(undefined);
    const skip = skips.get(item.id) ?? "";
    const flags: Flag[] = [];
    const flag = (code: FlagCode, message: string) => flags.push({ code, message });

    // Vendor terms: the preferred link, else the first one made. A build's are the CM's (its link to the item:
    // build price, MOQ, lead time; else the CM's defaults).
    let links = (linksOf.get(item.id) ?? [])
      .slice()
      .sort((a, b) => Number(b.is_preferred) - Number(a.is_preferred) || String(a.created_at).localeCompare(String(b.created_at)));
    if (spec) {
      const cm = vendorOf.get(spec.vendorId);
      links = links.filter((l) => l.vendor_id === spec.vendorId);
      if (!links.length) {
        links = [
          {
            id: `make:${item.id}`,
            vendor_id: spec.vendorId,
            item_id: item.id,
            vendor_sku: "",
            vendor_name: "",
            unit_price: null,
            currency: cm?.currency ?? "USD",
            price_breaks: [],
            moq: 0,
            order_multiple: 1,
            lead_time_days: null,
            is_preferred: true,
            created_at: "",
          },
        ];
      }
      if (spec.bom.status === "draft") flag("bom_draft", "Planned from its BOM's draft revision (none is active yet)");
      if (!cmSite(spec.vendorId))
        flag(
          "no_cm_site",
          `${cm?.short_name || cm?.name || "The CM"} has no CM site yet (set one up on the Consignment page), so parts can't ship there on a PO`,
        );
    }
    const link = links[0] ?? null;
    const vendor = link ? (vendorOf.get(link.vendor_id) ?? null) : null;
    if (!link) flag("no_vendor", "No vendor linked: can't be ordered until it has one");
    else if (!link.is_preferred && links.length > 1)
      flag("no_preferred", `No preferred vendor; using ${vendor?.short_name || vendor?.name || "the first link"}`);
    if (vendor?.archived) flag("vendor_archived", `${vendor.name} is archived`);
    // Terms: the link's, else the vendor's defaults, else the settings.
    const leadTimeFrom: ItemPlanResult["leadTimeFrom"] = link?.lead_time_days != null ? "link" : vendor?.lead_time_days != null ? "vendor" : "default";
    const leadTimeDefault = leadTimeFrom === "default";
    const leadTime =
      leadTimeFrom === "link" ? Number(link?.lead_time_days) : leadTimeFrom === "vendor" ? Number(vendor?.lead_time_days) : settings.default_lead_time_days;
    if (link && leadTimeDefault) flag("default_lead_time", `No lead time on the vendor link or the vendor; using the default ${leadTime} days`);
    const moqFrom: ItemPlanResult["moqFrom"] = Number(link?.moq ?? 0) > 0 ? "link" : Number(vendor?.moq ?? 0) > 0 ? "vendor" : null;
    const moq = moqFrom === "link" ? Number(link?.moq) : moqFrom === "vendor" ? Number(vendor?.moq) : 0;
    const multiple = Number(link?.order_multiple ?? 1) > 0 ? Number(link?.order_multiple ?? 1) : 1;
    if (link && !moq) flag("no_moq", "No MOQ on the vendor link or the vendor; ordering any quantity");
    const coverFrom: ItemPlanResult["coverFrom"] = vendor?.order_cycle_days != null ? "vendor" : "settings";
    const coverFor = coverFrom === "vendor" ? Number(vendor?.order_cycle_days) : settings.cover_days;
    // Cover through --- the vendor's own day, else the one for every vendor; bought items only.
    const ownCoverTo = !spec && vendor ? data.coverTo?.byVendor.get(vendor.id) : undefined;
    const coverToDay = spec ? null : (ownCoverTo ?? data.coverTo?.all ?? null);
    // Vendor closures --- the days its vendor is closed inside an order's lead time count on top of it.
    const closures = (vendor && closuresOf.get(vendor.id)) || [];
    const lands = (start: number) => landsOn(closures, start, leadTime);
    const latest = (need: number) => startBy(closures, need, leadTime);

    // Demand
    const f = forecastOf(item.id);
    const cls = clsOf.get(item.id) ?? null;
    if (!sell) {
      // Parts have no sales of their own: their demand is the builds'.
    } else if (!salesOf.has(item.id) && !f?.base && f?.from !== "like") flag("no_sales", "No sales in Shopify's history");
    else if (r.stopped)
      flag(
        "stopped_selling",
        f?.stopped ? "Out of stock 180+ days in a row: treated as stopped selling" : "No stock and no sales in 90 days: treated as stopped selling",
      );
    else if (r.historyDays < 60 && f?.from !== "like") flag("new_item", `Only ${r.historyDays} days of sales: the rate may move a lot`);
    const classTarget = cls === "A" ? settings.service_a : cls === "B" ? settings.service_b : settings.service_c;
    const serviceLevel = ip?.service_level != null ? Number(ip.service_level) : classTarget;
    const z = zFor(serviceLevel);
    // Stock policy: min/max and target levels set the floor themselves (and what an order tops up to).
    let stockPolicy: StockPolicy = ip?.stock_policy ?? "forecast";
    let minQty: number | null = null;
    let maxQty: number | null = null;
    if (stockPolicy === "min_max" && ip?.min_qty != null && ip?.max_qty != null) [minQty, maxQty] = [Number(ip.min_qty), Number(ip.max_qty)];
    else if (stockPolicy === "target" && ip?.target_qty != null) minQty = maxQty = Number(ip.target_qty);
    else stockPolicy = "forecast";
    const safetyFrom: ItemPlanResult["safetyFrom"] = minQty !== null ? "policy" : ip?.safety_stock != null ? "item" : "computed";
    const safetyStock = minQty !== null ? minQty : ip?.safety_stock != null ? Number(ip.safety_stock) : z * r.sd * Math.sqrt(leadTime + settings.review_days);

    // Supply
    const stock = stockOf.get(item.id) ?? { onHand: 0, available: 0, committed: 0 };
    if (stock.available < 0) flag("oversold", `Oversold: ${fmt(-stock.available)} more ordered than in stock`);
    const lines: Incoming[] = (linesOf.get(item.id) ?? []).map((l): Incoming => {
      const po = poOf.get(l.po_id) as PlanData["pos"][number];
      const promised = textOf(l.promised_date);
      const poArrive = textOf(po.expected_arrive);
      const poShip = textOf(po.expected_ship);
      const dateFrom: Incoming["dateFrom"] = promised ? "promised" : poArrive ? "po_arrive" : poShip ? "po_ship" : "lead_time";
      const date = promised ?? poArrive ?? poShip ?? isoOfDay(t0 + lands(dayNum(textOf(po.created_at) ?? today) - t0));
      const late = dayNum(date) < t0;
      return {
        poId: po.id,
        poNumber: po.number,
        lineId: l.id,
        qty: Math.max(0, forHhl(l)),
        arrive: late ? today : date,
        dateFrom,
        draft: po.status === "draft",
        late,
        kind: po.kind,
        shipTo: po.ship_to_location_id ?? null,
        ...(Number(l.qty_direct ?? 0) > 0 ? { direct: Number(l.qty_direct) } : {}),
      };
    });
    lines.sort((a, b) => a.arrive.localeCompare(b.arrive));
    // Supply to HHL; Pre-sales --- and every open make PO line with building left, backers' units included.
    const incoming = lines.filter((x) => x.qty > 0);
    const builds = lines.filter((x) => x.kind === "make");
    const lateLines = incoming.filter((x) => x.late);
    if (lateLines.length) flag("po_late", `${lateLines.map((x) => x.poNumber).join(", ")} ${lateLines.length > 1 ? "are" : "is"} past due`);
    const guessed = incoming.filter((x) => x.dateFrom === "po_ship" || x.dateFrom === "lead_time");
    if (guessed.length)
      flag(
        "po_date_guessed",
        `${guessed.map((x) => x.poNumber).join(", ")}: no arrival date, so ${guessed[0].dateFrom === "po_ship" ? "the ship date" : `order date + lead time${closures.length ? " (and the vendor's closures)" : ""}`} is used`,
      );
    const incomingQty = incoming.reduce((n, x) => n + x.qty, 0);
    const b2bLines = b2bOf.get(item.id) ?? [];

    // Projection --- far enough for an order placed at the end of the horizon to land and last. Vendor closures inside
    // that stretch it (H), but the stretch only counts for an item whose own order a closure moves; any other item is
    // planned over the plain projection (H0), exactly as without closures.
    const base = Math.max(settings.horizon_days + leadTime + coverFor + 1, coverToDay === null ? 0 : coverToDay + 1);
    const H0 = Math.min(maxH, base);
    const H = Math.min(maxH, landsOn(closures, 0, base));
    const { fc, b2b, inc, end } = buf;
    const overrides = overridesOf.get(item.id);
    // The forecast's rate for each day (its overrides included; a forecast that's stopped is 0 apart from
    // them). The stopped-selling rule stops an item the forecast doesn't: then only its overrides count.
    const stoppedByRule = r.stopped && !f?.stopped;
    const demandOn =
      f && !stoppedByRule
        ? (d: number) => f.dayRate(d < MONTH_TABLE_DAYS ? monthNo[d] : monthOfOffset(t0, d))
        : (d: number) => {
            const o = overrides?.get(d < maxH ? monthKey[d] : `${isoOfDay(t0 + d).slice(0, 7)}-01`);
            return o !== undefined ? o / (d < maxH ? monthLen[d] : MONTH_DAYS) : stoppedByRule ? 0 : r.rate;
          };
    if (f || overrides) for (let d = 0; d < H; d++) fc[d] = demandOn(d);
    else fc.fill(r.rate, 0, H);
    b2b.fill(0, 0, H);
    inc.fill(0, 0, H);
    let b2bOpen = 0;
    const b2bDue: { day: number; qty: number }[] = [];
    for (const b of b2bLines) {
      const d = Math.max(0, dayNum(textOf(b.due_date) ?? today) - t0);
      if (d < H) b2b[d] += Number(b.qty);
      b2bOpen += Number(b.qty);
      b2bDue.push({ day: d, qty: Number(b.qty) });
    }
    for (const x of incoming) {
      const d = dayNum(x.arrive) - t0;
      if (d < H) inc[d] += x.qty;
    }
    // Make planning --- builds' demand for it waits for stock, like B2B.
    // Stock already at a CM covers that CM's builds first (in date order); only the rest needs supplying.
    const dependent = (dependentOf.get(item.id) ?? []).slice().sort((a, b) => a.day - b.day);
    const siteLeft = new Map(siteStockOf.get(item.id) ?? []);
    let atSites = 0;
    for (const q of siteLeft.values()) atSites += Math.max(0, q);
    const depNet = dependent.map((x) => {
      const have = x.shipTo ? Math.max(0, siteLeft.get(x.shipTo) ?? 0) : 0;
      const take = Math.min(have, x.qty);
      if (take > 0 && x.shipTo) siteLeft.set(x.shipTo, have - take);
      return x.qty - take;
    });
    // Pre-sales --- units of runs that aren't for sale wait for stock like B2B; those needed at a CM (add-ons packed into
    // backers' boxes there) take what's at that CM first, after its builds.
    const reserved = (reservationsOf.get(item.id) ?? [])
      .map((x) => {
        const have = x.site ? Math.max(0, siteLeft.get(x.site) ?? 0) : 0;
        const fromSite = Math.min(have, x.qty);
        if (fromSite > 0 && x.site) siteLeft.set(x.site, have - fromSite);
        return { r: x, day: Math.max(0, dayNum(x.day) - t0), net: x.qty - fromSite, fromSite };
      })
      .sort((a, b) => a.day - b.day);
    let reservedQty = 0;
    for (const x of reserved) {
      if (x.day < H) b2b[x.day] += x.net;
      reservedQty += x.net;
    }
    let siteSpare = 0;
    for (const q of siteLeft.values()) siteSpare += Math.max(0, q);
    let dependentQty = 0;
    let depNetQty = 0;
    for (const [k, x] of dependent.entries()) {
      const d = Math.max(0, x.day);
      if (d < H) b2b[d] += depNet[k];
      dependentQty += x.qty;
      depNetQty += depNet[k];
    }
    let level = stock.available;
    let stockoutAt: number | null = null;
    let needAt: number | null = null;
    // Orders on their way (Mitch, 2026-11-03, from staff: items already on a PO "still show as suggested … as soon as we
    // go to 4, it freaks out"): until the last open PO for it lands, only running out is a need; dipping under the floor
    // meanwhile is what that PO is for (running out before it lands is At risk, and still a need).
    let onItsWay = -1;
    for (const x of incoming) onItsWay = Math.max(onItsWay, dayNum(x.arrive) - t0);
    for (let d = 0; d < H; d++) {
      // B2B orders wait for stock; forecast sales are lost when there's none.
      const before = level + inc[d] - b2b[d];
      level = before - fc[d];
      if (level < 0) level = Math.min(before, 0);
      end[d] = level;
      const demand = fc[d] + b2b[d] > 0;
      if (stockoutAt === null && level <= 0 && demand) stockoutAt = d;
      if (needAt === null && ((level < safetyStock && d >= onItsWay) || (level <= 0 && demand))) needAt = d;
    }
    // Vendor closures --- the projection this item is read over: the stretched one only when a closure moves its order.
    const Hu = needAt !== null && delayFor(closures, needAt, leadTime) ? H : H0;
    const needDay = needAt !== null && needAt < Hu ? needAt : null;
    const stockoutDay = stockoutAt !== null && stockoutAt < Hu ? stockoutAt : null;
    level = Hu > 0 ? end[Hu - 1] : stock.available;
    let dependentInH = 0;
    for (const [k, x] of dependent.entries()) if (Math.max(0, x.day) < Hu) dependentInH += depNet[k];
    const depRate = Hu > 0 ? dependentInH / Hu : 0;
    const totalRate = r.rate + depRate;
    // Cover past the projection: the forecast rate, with POs and B2B orders due later.
    let coverDays = stockoutDay;
    if (coverDays === null && totalRate > 0) {
      let tail = level;
      for (const x of incoming) if (dayNum(x.arrive) - t0 >= Hu) tail += x.qty;
      for (const b of b2bLines) if (dayNum(textOf(b.due_date) ?? today) - t0 >= Hu) tail -= Number(b.qty);
      for (const [k, x] of dependent.entries()) if (x.day >= Hu) tail -= depNet[k];
      for (const x of reserved) if (x.day >= Hu) tail -= x.net;
      coverDays = Hu + Math.max(0, Math.floor(tail / totalRate));
    }
    let next30 = 0;
    for (let d = 0; d < Math.min(30, H); d++) next30 += fc[d];
    const months = months6.map(({ m, len }, i) => {
      const o = overrides?.get(m);
      const due = (x: (typeof b2bLines)[number]) => textOf(x.due_date) ?? today;
      const b = b2bLines.filter((x) => monthOf(due(x)) === m || (i === 0 && due(x) < today)).reduce((n, x) => n + Number(x.qty), 0);
      const fm = f && !stoppedByRule ? f.months.find((x) => x.month === m) : undefined;
      if (fm) return { month: m, units: fm.units, override: fm.override !== null, b2b: b };
      return { month: m, units: o ?? (stoppedByRule ? 0 : r.rate) * len, override: o !== undefined, b2b: b };
    });

    // Suggestion: placed on the order-by day (today if that's passed), landing a lead time later ---
    // Vendor closures --- order-by is the latest day it still lands by the need day: a closure in the way moves it
    // earlier, and it may then land a little before the need day (the quantity still covers from the need day).
    const orderByDay = needDay === null ? null : latest(needDay);
    const arriveDay = orderByDay === null ? null : lands(Math.max(0, orderByDay));
    const delay = needDay === null ? null : delayFor(closures, needDay, leadTime);
    // Cover through --- falling below the floor before that day: due now, and it lasts to then at least.
    const short = coverToDay !== null && needDay !== null && needDay <= coverToDay;
    let rawQty = 0;
    if (arriveDay !== null && needDay !== null) {
      const arrive = Math.max(arriveDay, needDay);
      const span = short ? Math.max(coverFor, (coverToDay as number) - arrive + 1) : coverFor;
      if (arrive < Hu) rawQty = orderQty(arrive, arrive > 0 ? end[arrive - 1] : stock.available, Hu, span, safetyStock, maxQty);
    }
    const terms = link ? { ...link, moq } : undefined;
    const qty = !rawQty ? 0 : terms ? validQty(terms, rawQty) : rawQty;
    const unitPrice = terms ? priceFor(terms, qty || validQty(terms, 0)) : null;
    if (link && unitPrice == null)
      flag(
        "no_price",
        spec ? `No build price: link it to ${vendor?.short_name || vendor?.name || "the CM"} with the CM's price` : "No price on the vendor link",
      );
    const currency = link?.currency || vendor?.currency || "USD";
    const cost = qty && unitPrice != null ? mulRound(qty, unitPrice, 2) : null;

    // A cheaper price break a little further up, unless it would buy into overstock (builds' demand counts).
    const priceHint = terms ? priceHintFor(terms, qty, unitPrice, totalRate, coverFor, overstockDays) : null;

    // Risk, overstock and the bucket ---
    const lateArrival = stockoutDay === null ? undefined : incoming.find((x) => dayNum(x.arrive) - t0 > (stockoutDay as number));
    const lateNow = lateLines.length > 0 && stock.available <= safetyStock;
    const atRisk = !skip && (!!lateArrival || lateNow);
    const riskNote = lateArrival
      ? `${lateArrival.poNumber} lands ${shortDate(lateArrival.arrive, today)}, after stock runs out ${shortDate(isoOfDay(t0 + (stockoutDay as number)), today)}`
      : lateNow
        ? `${lateLines[0].poNumber} is past due and stock is at safety stock`
        : "";
    const totalSupply = stock.available + incomingQty + siteSpare;
    const overstock = totalSupply > 0 && (totalRate === 0 ? b2bOpen + depNetQty + reservedQty < totalSupply : (coverDays ?? 0) > overstockDays);
    const snoozeUntil = textOf(ip?.snooze_until);
    const snoozed = !!snoozeUntil && snoozeUntil > today;
    let bucket: Bucket = "ok";
    if (skip) bucket = "not_planned";
    else if (snoozed) bucket = "snoozed";
    else if (qty > 0 && orderByDay !== null && (orderByDay <= settings.review_days || short)) bucket = "order_now";
    else if (atRisk) bucket = "at_risk";
    else if (qty > 0 && orderByDay !== null && orderByDay <= 30) bucket = "this_month";
    else if (qty > 0 && orderByDay !== null && orderByDay <= settings.horizon_days) bucket = "upcoming";
    else if (overstock) bucket = "overstock";

    return {
      item,
      vendor,
      link,
      terms: terms ?? null,
      policy: ip?.policy ?? "manual",
      planned: !skip,
      skip,
      cls,
      rate: r.rate,
      rate90: r.rate90,
      rate365: r.rate365,
      sd: r.sd,
      historyDays: r.historyDays,
      forecast: f,
      demandOn,
      next30,
      months,
      b2bOpen,
      b2bDue,
      stockPolicy,
      minQty,
      maxQty,
      serviceLevel,
      serviceFrom: ip?.service_level != null ? "item" : "class",
      z,
      safetyStock,
      safetyFrom,
      leadTime,
      leadTimeFrom,
      leadTimeDefault,
      closures,
      landsOn: lands,
      startBy: latest,
      moq,
      moqFrom,
      multiple,
      coverFor,
      coverFrom,
      coverTo: coverToDay === null ? null : { day: coverToDay, from: ownCoverTo !== undefined ? "vendor" : "all", short },
      onHand: stock.onHand,
      available: stock.available,
      committed: stock.committed,
      incoming,
      builds,
      incomingQty,
      stockoutDay,
      coverDays,
      needDay,
      orderByDay,
      horizon: Hu,
      arriveDay,
      delay,
      rawQty,
      qty,
      unitPrice,
      currency,
      cost,
      priceHint,
      atRisk,
      riskNote,
      overstock,
      bucket,
      flags,
      snoozedUntil: snoozed ? snoozeUntil : null,
      snoozeReason: snoozed ? (ip?.snooze_reason ?? "") : "",
      role: spec ? "make" : "buy",
      make: spec,
      level: levelOf?.get(item.id) ?? -1,
      dependent,
      depNet,
      dependentQty,
      depRate,
      atSites,
      shipTo: defaultShipTo(spec, vendor),
      orders: [],
      reserved,
      reservedQty,
    };
  };

  // Make planning ---------------------------------------------------------------------------------------
  /** Where an item's own demand (sales, B2B) is served from: the vendor's usual ship-to, else HHL. A build ships home. */
  const defaultShipTo = (spec: MakeSpec | null, vendor: PlanVendor | null) => {
    const own = vendor?.default_ship_to_location_id ?? null;
    if (spec) return own && own !== cmSite(spec.vendorId) ? own : hhlDefault;
    return own ?? hhlDefault;
  };
  const planLike = { today, settings } as Plan;

  /** An item in a BOM: its orders for a year with what each is for; a build's go on to its parts. */
  const runMrp = (r: ItemPlanResult) => {
    const planned = plannedPurchases(planLike, MRP_DAYS, [r], { snoozed: true });
    const Hp = landsOn(r.closures, 0, MRP_DAYS + r.leadTime + r.coverFor);
    const demands: PegDemand[] = [
      ...weeklyDemand(r.demandOn, Hp),
      ...r.b2bDue.map((b) => ({ kind: "b2b" as const, day: b.day, qty: b.qty, demand: null })),
      ...r.dependent.map((x, k) => ({ kind: "build" as const, day: Math.max(0, x.day), qty: r.depNet[k], demand: x })),
      ...r.reserved.map((x) => ({ kind: "reserved" as const, day: x.day, qty: x.net, demand: null, shipTo: x.r.site })), // Pre-sales ---
    ];
    const pegs = peg(
      [
        { key: "stock", day: 0, qty: Math.max(0, r.available) },
        ...r.incoming.map((x) => ({ key: `po:${x.lineId}`, day: dayNum(x.arrive) - t0, qty: x.qty })),
        ...planned.map((o, n) => ({ key: `o:${n}`, day: o.arriveDay, qty: o.qty })),
      ],
      demands,
    );
    // Orders pegged to nothing (safety stock, MOQ) ship where most of the item's demand goes.
    const own = r.shipTo;
    const home = new Map<string | null, number>();
    for (const [k, x] of r.dependent.entries()) home.set(x.shipTo, (home.get(x.shipTo) ?? 0) + r.depNet[k]);
    for (const x of r.reserved) if (x.r.site) home.set(x.r.site, (home.get(x.r.site) ?? 0) + x.net); // Pre-sales --- packed at the CM
    const independent = demands.reduce((n, d) => n + (d.kind === "build" || (d.kind === "reserved" && d.shipTo) ? 0 : d.qty), 0);
    if (independent > 0) home.set(own, (home.get(own) ?? 0) + independent);
    const homeShipTo = [...home].sort((a, b) => b[1] - a[1])[0]?.[0] ?? own;
    r.orders = planned.map((o, n): MrpOrder => {
      const mine = pegs.get(`o:${n}`) ?? [];
      const split = new Map<string | null, number>();
      for (const p of mine) {
        const to = p.kind === "build" ? (p.demand?.shipTo ?? null) : p.kind === "reserved" && p.shipTo ? p.shipTo : own;
        split.set(to, (split.get(to) ?? 0) + p.qty);
      }
      const shipSplit = [...split].map(([shipTo, qty]) => ({ shipTo, qty })).sort((a, b) => b.qty - a.qty);
      const chain = chainOf(mine);
      const unitPrice = r.terms ? priceFor(r.terms, o.qty) : null;
      return {
        key: `${r.item.id}:${n}`,
        itemId: r.item.id,
        kind: r.role === "make" ? "make" : "purchase",
        vendorId: r.vendor?.id ?? null,
        shipTo: shipSplit[0]?.shipTo ?? homeShipTo,
        shipSplit: shipSplit.length > 1 ? shipSplit : [],
        orderBy: r.startBy(o.needDay),
        orderDay: o.orderDay,
        arriveDay: o.arriveDay,
        needDay: o.needDay,
        qty: o.qty,
        unitPrice,
        cost: unitPrice == null ? null : mulRound(o.qty, unitPrice, 2),
        currency: r.currency,
        first: o.first,
        leadTime: r.leadTime,
        delay: delayFor(r.closures, o.needDay, r.leadTime),
        cumLead: o.arriveDay - o.orderDay + chain.reduce((t, s) => t + s.lead, 0),
        chain,
        pegs: mine,
        roots: rootsOf(r.item.id, mine),
        priceHint: r.terms ? priceHintFor(r.terms, o.qty, unitPrice, r.rate + r.depRate, r.coverFor, overstockDays) : null,
      };
    });
    const first = r.orders[0];
    if (first) {
      r.shipTo = first.shipTo;
      if (first.shipSplit.length > 1) {
        const names = first.shipSplit.map((x) => `${fmt(Math.round(x.qty))} to ${x.shipTo ? nameOf.get(x.shipTo) || "a CM" : "HHL"}`);
        r.flags.push({ code: "ship_split", message: `Its next order is for more than one place: ${names.join(", ")}` });
      }
    } else if (r.dependent.length) r.shipTo = homeShipTo;
    const spec = r.make;
    // The builds' parts are needed at the CM on the day each build starts (today at the earliest).
    const explode = (x: Omit<DependentDemand, "qty" | "parentId" | "shipTo" | "vendorId">, vendorId: string) => {
      if (!spec) return;
      const site = cmSite(vendorId);
      const add = (itemId: string, qty: number) => {
        if (qty <= 1e-9) return;
        const list = dependentOf.get(itemId);
        const need = { ...x, qty, parentId: r.item.id, vendorId, shipTo: site };
        if (list) list.push(need);
        else dependentOf.set(itemId, [need]);
      };
      // A sub-assembly built in the same order comes from its own stock first: only the rest needs its parts.
      const covered = new Map<string, number>();
      for (const b of spec.builtIn) {
        const left = stockLeft(b.itemId, site);
        const use = Math.min(left, x.buildQty * b.qty);
        if (use <= 0) continue;
        insideStock.set(b.itemId, left - use); // (it isn't planned itself: nothing to order for it)
        for (const p of b.parts) covered.set(p.itemId, (covered.get(p.itemId) ?? 0) + use * p.qty);
      }
      for (const p of spec.parts) add(p.itemId, x.buildQty * p.qty - (covered.get(p.itemId) ?? 0));
    };
    // Open make POs still to be built take their parts at the CM too, from the day they should start. With its make
    // order (consignment): only the units not yet reported built (their parts are still in the CM's stock), by the
    // BOM snapshot it was made with, at its CM site; without one, what hasn't arrived, by the current BOM.
    // Pre-sales --- backers' units still to build count too, on a line that brings HHL nothing.
    for (const x of r.builds ?? []) {
      const vendorId = poOf.get(x.poId)?.vendor_id ?? spec?.vendorId ?? "";
      const end = dayNum(x.arrive) - t0;
      const by = r.startBy(end);
      const start = Math.max(0, by);
      const pegged = rootsOf(r.item.id, pegs.get(`po:${x.lineId}`) ?? []);
      const mo = makeOrderOf.get(x.lineId);
      const units = mo ? Math.max(0, (lineQtyOf.get(x.lineId) ?? x.qty) - Number(mo.qty_output)) : x.qty;
      if (units <= 0) continue;
      const site = mo?.cm_location_id ?? cmSite(vendorId);
      const need = {
        day: start,
        buildQty: units,
        buildDay: start,
        buildEnd: end,
        poNumber: x.poNumber,
        chain: [{ itemId: r.item.id, vendorId, lead: r.landsOn(by) - by, shipTo: x.shipTo ?? null }],
        roots: pegged.length ? pegged : [{ itemId: r.item.id, from: end, to: end, qty: units }],
      };
      if (!mo) {
        explode(need, vendorId);
        continue;
      }
      for (const c of componentsOf(mo)) {
        if (!isConsigned(c)) continue;
        const list = dependentOf.get(c.item_id);
        const row = { ...need, qty: units * Number(c.per_unit), parentId: r.item.id, vendorId, shipTo: site };
        if (list) list.push(row);
        else dependentOf.set(c.item_id, [row]);
        // A sub-assembly an open make order takes from stock isn't there for the builds planned after it.
        if (spec?.builtIn.some((b) => b.itemId === c.item_id)) insideStock.set(c.item_id, Math.max(0, stockLeft(c.item_id, site) - row.qty));
      }
    }
    // Builds still to order, after the open ones have taken their share of any sub-assembly in stock.
    for (const o of spec ? r.orders : []) {
      const step: ChainStep = { itemId: r.item.id, vendorId: spec?.vendorId ?? "", lead: o.arriveDay - o.orderDay, shipTo: o.shipTo };
      explode(
        { day: o.orderDay, buildQty: o.qty, buildDay: o.orderDay, buildEnd: o.arriveDay, poNumber: null, chain: [step, ...o.chain], roots: o.roots },
        spec?.vendorId ?? "",
      );
    }
  };

  let results: ItemPlanResult[];
  if (!graph) results = items.map(planOne);
  else {
    // Level by level: an item after everything that uses it, so its builds' demand is in when it's planned.
    const byId = new Map<string, ItemPlanResult>();
    const order = items.map((item, i) => ({ item, i, level: levelOf?.get(item.id) ?? -1 })).sort((a, b) => a.level - b.level || a.i - b.i);
    for (const { item, level } of order) {
      const r = planOne(item);
      byId.set(item.id, r);
      if (level >= 0) runMrp(r);
    }
    results = items.map((i) => byId.get(i.id) as ItemPlanResult);
    for (const spec of graph.make.values()) cmSite(spec.vendorId);
  }

  return {
    today,
    settings,
    results,
    byItem: new Map(results.map((r) => [r.item.id, r])),
    hasSales: data.sales.length > 0,
    mrp: graph ? { graph, siteOf, hhl: hhlDefault, names: nameOf } : null,
  };
}

// Reading the plan ---------------------------------------------------------------------------------------

/** Rows for a bucket tab. "At risk" also lists at-risk items that are in another bucket (e.g. Order now). */
export function inBucket(r: ItemPlanResult, bucket: Bucket): boolean {
  return bucket === "at_risk" ? r.atRisk && r.bucket !== "snoozed" && r.bucket !== "not_planned" : r.bucket === bucket;
}

/** The order-by date in words: "Now (12 days late)", "Nov 20". */
export function orderByText(r: ItemPlanResult, today: string): string {
  if (r.orderByDay === null) return "";
  if (r.orderByDay < 0) return `Now (${fmt(-r.orderByDay)} day${r.orderByDay === -1 ? "" : "s"} late)`;
  if (r.orderByDay === 0) return "Today";
  return shortDate(addPlanDays(today, r.orderByDay), today);
}

// Projected stock timeline (N6) -------------------------------------------------------------------------------

export type TimelineEntry = {
  /** Unique within the timeline. */
  key: string;
  /** Projection day (0 = today) and its date; a run of sales is dated its last day. */
  day: number;
  date: string;
  kind: "start" | "po" | "build" | "b2b" | "parts" | "reserved" | "sales";
  label: string;
  /** In (+) or out (−) of HHL's available stock. */
  qty: number;
  /** Forecast sales there was no stock for (lost, as the plan counts them). */
  lost?: number;
  /** Available after it. */
  balance: number;
  poId?: string;
  draft?: boolean;
  late?: boolean;
};

/**
 * The item's stock at HHL the way the plan projects it, day by day from today's available: POs and builds land, then
 * what waits for stock is taken (B2B orders, parts the CMs' builds need beyond what's at the CM, units held for
 * pre-sales: these can take it below zero), then the forecast's sales, which are lost when there's none (never below
 * zero). Each landing and each thing due is a row; the sales between them are one row (at most a week), dated its last
 * day. Committed supply and demand only: the plan's suggestion isn't in it. `days` (90) is stretched to the plan's own
 * horizon and to the last landing or thing due, up to 180. `floor` is what the balance is read against (safety stock, or
 * the min of min/max); `stockoutDay` / `belowDay` are read off these same days by the plan's rule, so the headline
 * always agrees with the rows.
 */
export function stockTimeline(
  r: ItemPlanResult,
  today: string,
  days = 90,
): { entries: TimelineEntry[]; floor: number; stockoutDay: number | null; belowDay: number | null } {
  const date = (d: number) => addPlanDays(today, d);
  type Event = Omit<TimelineEntry, "balance" | "date" | "key">;
  const supply = new Map<number, Event[]>();
  const due = new Map<number, Event[]>();
  const put = (map: Map<number, Event[]>, e: Event) => map.set(e.day, [...(map.get(e.day) ?? []), e]);
  for (const x of r.incoming) {
    const day = Math.max(0, dayNum(x.arrive) - dayNum(today));
    put(supply, { day, kind: x.kind === "make" ? "build" : "po", label: x.poNumber, qty: x.qty, poId: x.poId, draft: x.draft, late: x.late });
  }
  for (const b of r.b2bDue) put(due, { day: Math.max(0, b.day), kind: "b2b", label: "B2B order due", qty: -b.qty });
  r.dependent.forEach((x, k) => {
    const net = r.depNet[k] ?? x.qty;
    if (net > 0) put(due, { day: Math.max(0, x.day), kind: "parts", label: x.poNumber ? `Parts for ${x.poNumber}` : "Parts for a planned build", qty: -net });
  });
  for (const x of r.reserved)
    if (x.net > 0) put(due, { day: Math.max(0, x.day), kind: "reserved", label: `${x.r.label || "Held"} (${x.r.runNumber})`, qty: -x.net });
  const last = Math.max(0, ...supply.keys(), ...due.keys());
  const horizon = Math.min(180, Math.max(days, last + 1, r.horizon));
  const floor = r.minQty ?? r.safetyStock;
  let stockoutDay: number | null = null;
  let belowDay: number | null = null;

  let level = r.available;
  const entries: TimelineEntry[] = [{ key: "start", day: 0, date: today, kind: "start", label: "Available now", qty: 0, balance: level }];
  const add = (e: Event) => entries.push({ ...e, key: `${e.kind}:${entries.length}`, date: date(e.day), balance: level });
  // Sales since the last row: shown once they come to half a unit or more (smaller amounts carry on), at the latest weekly.
  let sales = { from: 0, sold: 0, lost: 0 };
  const flush = (through: number) => {
    if (through >= sales.from && (sales.sold >= 0.5 || sales.lost >= 0.5)) {
      const span = through > sales.from ? `${shortDate(date(sales.from), today)}–${shortDate(date(through), today)}` : shortDate(date(through), today);
      entries.push({
        key: `sales:${entries.length}`,
        day: through,
        date: date(through),
        kind: "sales",
        label: span, // the kind says it's the forecast's sales
        qty: -sales.sold,
        ...(sales.lost >= 0.5 ? { lost: sales.lost } : {}),
        balance: level,
      });
      sales = { from: through + 1, sold: 0, lost: 0 };
    }
  };
  for (let d = 0; d < horizon; d++) {
    const ins = supply.get(d) ?? [];
    const outs = due.get(d) ?? [];
    if (ins.length || outs.length || (d > sales.from && (d - sales.from) % 7 === 0)) flush(d - 1);
    for (const e of ins) {
      level += e.qty;
      add(e);
    }
    for (const e of outs) {
      level += e.qty;
      add(e);
    }
    // The plan's rule: B2B and builds wait for stock (above), forecast sales are lost when there's none.
    const before = level;
    const demand = r.demandOn(d);
    level = before - demand;
    if (level < 0) level = Math.min(before, 0);
    sales.sold += before - level;
    sales.lost += demand - (before - level);
    // When it runs out and falls below its floor, as the plan reads its projection (over these rows' days).
    const wanted = demand > 0 || outs.length > 0;
    if (stockoutDay === null && level <= 0 && wanted) stockoutDay = d;
    if (belowDay === null && (level < floor || (level <= 0 && wanted))) belowDay = d;
  }
  flush(horizon - 1);
  return { entries, floor, stockoutDay, belowDay };
}

/** Days of cover in words: "12 days", "more than 365 days", "out". */
export function coverText(r: ItemPlanResult): string {
  if (r.coverDays === null) return r.rate === 0 && r.b2bOpen === 0 && !r.dependentQty ? (r.item.kind === "sell" ? "no sales" : "no demand") : "—";
  if (r.coverDays === 0) return "out";
  return `${r.stockoutDay === null ? "~" : ""}${fmt(r.coverDays)} day${r.coverDays === 1 ? "" : "s"}`;
}

export type VendorGroup = {
  vendor: PlanVendor | null;
  key: string;
  /** Make planning --- one group per vendor, ship-to and kind (a build is a make PO). */
  shipTo: string | null;
  kind: MrpOrder["kind"];
  rows: ItemPlanResult[];
  lines: number;
  units: number;
  cost: number;
  currency: string;
  unpriced: number;
  /** Lines the MOQ or order multiple raised, and by how many units in all. */
  moqLines: number;
  moqUnits: number;
};

/** Make planning --- One draft PO per vendor, ship-to and kind: the key groups (and auto mode's drafts) share. */
export const orderGroupKey = (vendorId: string | null, shipTo: string | null, kind: MrpOrder["kind"]) => `${vendorId ?? "none"}|${shipTo ?? ""}|${kind}`;

/** Rows grouped by vendor (no vendor last), each with the suggestion's totals; `qtyOf` lets the UI use edited quantities. */
export function groupByVendor(rows: ItemPlanResult[], qtyOf: (r: ItemPlanResult) => number = (r) => r.qty): VendorGroup[] {
  const groups = new Map<string, VendorGroup>();
  for (const r of rows) {
    const kind = r.role === "make" ? "make" : "purchase";
    const key = orderGroupKey(r.vendor?.id ?? null, r.shipTo, kind);
    let g = groups.get(key);
    if (!g) {
      g = { vendor: r.vendor, key, shipTo: r.shipTo, kind, rows: [], lines: 0, units: 0, cost: 0, currency: r.currency, unpriced: 0, moqLines: 0, moqUnits: 0 };
      groups.set(key, g);
    }
    g.rows.push(r);
    const q = qtyOf(r);
    if (q <= 0) continue;
    g.lines += 1;
    g.units += q;
    const price = r.link ? priceFor(r.link, q) : null;
    if (price == null) g.unpriced += 1;
    else g.cost = mulRound(g.cost + mulRound(q, price, 2), 1, 2);
    if (r.qty > r.rawQty && q === r.qty) {
      g.moqLines += 1;
      g.moqUnits += r.qty - r.rawQty;
    }
  }
  return [...groups.values()].sort(
    (a, b) =>
      Number(!a.vendor) - Number(!b.vendor) ||
      b.lines - a.lines ||
      (a.vendor?.short_name || a.vendor?.name || "").localeCompare(b.vendor?.short_name || b.vendor?.name || ""),
  );
}

// Planned purchases calendar ---------------------------------------------------------------------------------

export type PlannedOrder = {
  result: ItemPlanResult;
  /** Days from today. */
  orderDay: number;
  arriveDay: number;
  /** The month it's ordered in (its first day): when the money goes out. */
  month: string;
  qty: number;
  cost: number | null;
  currency: string;
  /** The day stock would fall below its floor without it (the order is placed a lead time before, today at the earliest). */
  needDay: number;
  /** The order the plan suggests now; later ones are what the simulation expects to follow. */
  first: boolean;
};

/** Each day's month (its first day) from day t0, kept for the last few starts (the plan runs this once per item). */
const monthTables = new Map<string, string[]>();
function monthsFrom(t0: number, days: number): string[] {
  const key = `${t0}|${days}`;
  let table = monthTables.get(key);
  if (!table) {
    if (monthTables.size > 8) monthTables.clear();
    table = Array.from({ length: days }, (_, d) => monthOf(isoOfDay(t0 + d)));
    monthTables.set(key, table);
  }
  return table;
}

/**
 * The orders the plan would place over the next `days` (a year): each planned item's projection run
 * forward, ordering a lead time before stock would fall below its floor, the quantity worked out like the
 * suggestion (order cycle, min/max, MOQ, multiple), each order counted as arriving from then on. Orders are
 * at least a review period apart; a target level reorders once a review period's sales have gone (min = max
 * would otherwise top up by a day's sales every day). For cash planning by month and vendor. Snoozed and
 * not-planned items are left out.
 */
export function plannedPurchases(
  plan: Plan,
  days = 365,
  results: readonly ItemPlanResult[] = plan.results,
  /** Make planning --- MRP plans snoozed builds too (a snooze hides the row; the parts still need ordering). */
  opts: { snoozed?: boolean } = {},
): PlannedOrder[] {
  const out: PlannedOrder[] = [];
  const t0 = dayNum(plan.today);
  const review = Math.max(1, plan.settings.review_days);
  const monthAt = monthsFrom(t0, days);
  for (const r of results) {
    if (!r.planned || (r.bucket === "snoozed" && !opts.snoozed)) continue;
    const H = Math.min(2000, landsOn(r.closures, 0, days + r.leadTime + r.coverFor + 1));
    if (buf.fc.length < H) buf = { fc: new Float64Array(H), b2b: new Float64Array(H), inc: new Float64Array(H), end: new Float64Array(H) };
    const { fc, b2b, inc, end } = buf;
    let demand = 0;
    for (let d = 0; d < H; d++) {
      fc[d] = r.demandOn(d);
      demand += fc[d];
    }
    if (demand <= 0 && r.b2bOpen <= 0 && !r.depNet.some((q) => q > 0) && !(r.reservedQty > 0)) continue;
    b2b.fill(0, 0, H);
    inc.fill(0, 0, H);
    for (const b of r.b2bDue) if (b.day < H) b2b[b.day] += b.qty;
    // Pre-sales --- units not for sale (backers, holds) after what's at their CM.
    for (const x of r.reserved ?? []) if (x.day < H) b2b[x.day] += x.net;
    // Builds' demand for it, after stock already at their CM.
    for (const [k, x] of r.dependent.entries()) if (Math.max(0, x.day) < H) b2b[Math.max(0, x.day)] += r.depNet[k];
    for (const x of r.incoming) {
      const d = dayNum(x.arrive) - t0;
      if (d < H) inc[d] += x.qty;
    }
    const trigger = r.stockPolicy === "target" ? r.safetyStock - r.rate * review : r.safetyStock;
    // Quantities and prices for many orders: whole MOQs and multiples and a flat price (the usual case) skip the
    // exact decimal helpers; a cash calendar to the cent doesn't need them.
    const moq = Number(r.terms?.moq ?? 0);
    const simple = Number.isInteger(r.multiple) && Number.isInteger(moq);
    const roundQty = (raw: number) =>
      !raw ? 0 : !r.terms ? raw : simple ? Math.max(1, Math.ceil(Math.max(raw, moq) / r.multiple)) * r.multiple : validQty(r.terms, raw);
    const priceBreaks = r.terms?.price_breaks;
    const breaks = Array.isArray(priceBreaks) && priceBreaks.length > 0;
    const flatPrice = r.terms?.unit_price == null ? null : Number(r.terms.unit_price);
    // One pass forward: on a day stock falls below the floor, order a lead time before (today at the
    // earliest). Orders count from that day on (or from when they land, if later), so the days already projected
    // don't change. Vendor closures --- inside the lead time they move the order earlier (r.startBy()); one that
    // then lands before the day it's needed counts from that day.
    let from = 0;
    let level = r.available;
    let placed = false;
    for (let d = 0; d < H; d++) {
      const before = level + inc[d] - b2b[d];
      level = before - fc[d];
      if (level < 0) level = Math.min(before, 0);
      end[d] = level;
      if (d < from || !(level < trigger || (level <= 0 && fc[d] + b2b[d] > 0))) continue;
      const orderDay = Math.max(0, r.startBy(d));
      const lands = r.landsOn(orderDay);
      const arrive = Math.max(d, lands);
      if (orderDay >= days || arrive >= H) break;
      // Stock the day before it lands: projected ahead from today (nothing else lands in between).
      let ahead = arrive > d ? level : arrive > 0 ? end[arrive - 1] : r.available;
      for (let k = d + 1; k < arrive; k++) {
        const b = ahead + inc[k] - b2b[k];
        ahead = b - fc[k];
        if (ahead < 0) ahead = Math.min(b, 0);
      }
      const qty = roundQty(orderQty(arrive, ahead, H, r.coverFor, r.safetyStock, r.maxQty));
      if (!qty) {
        from = arrive + 1;
        continue;
      }
      const price = !r.terms ? null : breaks ? priceFor(r.terms, qty) : flatPrice;
      out.push({
        result: r,
        orderDay,
        arriveDay: lands,
        month: monthAt[orderDay],
        qty,
        cost: price == null ? null : Math.round(qty * price * 100) / 100,
        currency: r.currency,
        needDay: d,
        first: !placed,
      });
      placed = true;
      inc[arrive] += qty;
      if (arrive === d) {
        // It lands today: today's stock again, with it.
        const b = (d > 0 ? end[d - 1] : r.available) + inc[d] - b2b[d];
        level = b - fc[d];
        if (level < 0) level = Math.min(b, 0);
        end[d] = level;
      }
      from = arrive + review; // the next order goes in at a later review
    }
  }
  return out;
}

/** Make planning --- an order's own share of its cumulative lead time (its lead time, closures included). */
export const ownLead = (o: Pick<MrpOrder, "cumLead" | "chain">) => o.cumLead - o.chain.reduce((t, s) => t + s.lead, 0);

/**
 * Vendor closures --- what the order-by sentence adds to "(30-day lead time": the days closed inside it, or, when it's
 * needed during a closure, that it lands before they close.
 */
function leadNote(r: ItemPlanResult, date: (d: number) => string): string {
  if (!r.delay) return "";
  if (!r.delay.late && r.arriveDay !== null && r.needDay !== null && r.arriveDay < r.needDay) return `, landing ${date(r.arriveDay)} before they close`;
  return ` + ${r.delay.days} day${r.delay.days === 1 ? "" : "s"} closed`;
}

/** The reasoning behind a row, one plain sentence each, for the "why" popover and the MCP. */
export function explain(r: ItemPlanResult, plan: Pick<Plan, "today" | "settings"> & Partial<Pick<Plan, "byItem" | "mrp">>): string[] {
  const { today, settings } = plan;
  const date = (d: number) => shortDate(addPlanDays(today, d), today);
  const out: string[] = [];
  if (!r.planned) return [r.skip];
  if (r.bucket === "snoozed") out.push(`Snoozed until ${shortDate(r.snoozedUntil as string, today)}${r.snoozeReason ? `: ${r.snoozeReason}` : ""}.`);
  const f = r.forecast;
  if (r.flags.some((x) => x.code === "stopped_selling")) {
    out.push(
      f?.stopped
        ? "Out of stock 180+ days in a row with no sales, so it's treated as stopped selling. Add a forecast override to plan it."
        : `No stock and no sales in 90 days, so it's treated as stopped selling (${fmt(r.rate365)}/day over the year). Add a forecast override to plan it.`,
    );
  } else if (f && (r.rate > 0 || f.from !== "summary")) {
    // The forecast's own why (base rate, seasonality, trend, adjustments, overrides), then what it comes to.
    out.push(...f.why().slice(0, -1), `Forecast ${fmt(r.rate)}/day over the next 90 days (${fmt(Math.round(r.next30))} in the next 30).`);
  } else if (r.rate > 0) {
    out.push(
      `Sells ${fmt(r.rate)}/day: half the last 90 days (${fmt(r.rate90)}/day), half the last ${r.historyDays >= 365 ? "12 months" : `${r.historyDays} days`} (${fmt(r.rate365)}/day).`,
    );
  } else if (r.item.kind === "sell") out.push("No sales to plan from.");
  // Make planning --- what it's built from, and the builds that need it.
  if (r.make) {
    const cm = r.vendor ? r.vendor.short_name || r.vendor.name : "its CM";
    out.push(
      `Built by ${cm} from its BOM (Rev ${r.make.bom.revision}${r.make.bom.status === "draft" ? ", draft" : ""}): ${r.make.parts.length} part${r.make.parts.length === 1 ? "" : "s"} HHL consigns${r.make.cmBuys.length ? `, ${r.make.cmBuys.length} the CM buys` : ""}. Each build's parts are needed at ${cm} the day it starts.`,
    );
  }
  if (r.dependent.length) {
    const sku = (id: string) => plan.byItem?.get(id)?.item.sku || "a build";
    const shown = r.dependent
      .slice(0, 3)
      .map((x) => `${fmt(Math.round(x.qty))} for ${x.poNumber ? `${x.poNumber} (${sku(x.parentId)})` : sku(x.parentId)} ${x.day <= 0 ? "now" : date(x.day)}`);
    out.push(
      `Builds use it: ${fmt(Math.round(r.dependentQty))} in all (${shown.join("; ")}${r.dependent.length > 3 ? `; ${r.dependent.length - 3} more` : ""})${r.rate > 0 ? ", on top of its own sales" : ""}.`,
    );
    const fromSites = r.dependentQty - r.depNet.reduce((n, q) => n + q, 0);
    if (fromSites > 0) out.push(`${fmt(Math.round(fromSites))} of that is already at the CMs that build them (${fmt(Math.round(r.atSites))} there in all).`);
  }
  if ((!f || r.flags.some((x) => x.code === "stopped_selling")) && r.months.some((m) => m.override)) {
    out.push(
      `Forecast overridden for ${r.months
        .filter((m) => m.override)
        .map((m) => `${monthLabel(m.month)} (${fmt(m.units)})`)
        .join(", ")}.`,
    );
  }
  if (r.b2bOpen > 0) out.push(`Wholesale/B2B orders: ${fmt(r.b2bOpen)} units still to ship.`);
  // Pre-sales --- units of runs that aren't for sale, and what's still planned for them.
  const groups = new Map<string, NonNullable<ItemPlanResult["reserved"]>>();
  for (const x of r.reserved ?? []) {
    const key = `${x.r.runId}|${x.r.kind}|${x.r.label}`;
    groups.set(key, [...(groups.get(key) ?? []), x]);
  }
  for (const list of groups.values()) {
    const res = list[0].r;
    const left = list
      .filter((x) => x.net > 0 || x.fromSite > 0)
      .map((x) => {
        const when = x.day <= 0 ? "now" : date(x.day);
        const planned = `${fmt(Math.round(x.net))} planned as demand ${when}`;
        if (!x.fromSite) return planned;
        const there = `${fmt(Math.round(x.fromSite))} of them come from what's at ${plan.mrp?.names.get(x.r.site ?? "") || "the CM"} already`;
        return x.net > 0 ? `${there}; ${planned}` : there;
      })
      .join("; ");
    const why = list.map((x) => x.r.why).find((w) => /in stock already/.test(w)) ?? res.why;
    if (res.kind === "held")
      out.push(
        `Held on ${res.runNumber}: ${fmt(res.reserved)}${res.label && res.label !== "held" ? ` (${res.label})` : ""}, not for sale. ${why}.${left ? ` ${left}.` : ""}`,
      );
    else
      out.push(
        `Reserved for ${res.label} (${res.runNumber}): ${fmt(res.reserved)}${res.shipped ? `, ${fmt(res.shipped)} shipped` : ""}. ${why}.${left ? ` ${left}.` : ""}`,
      );
  }
  // Decision 7's exception: a pre-sale says which of the product's units are the campaign's.
  if (PRE_ORDER.test(`${r.item.shopify_product_title} ${r.item.name}`))
    out.push(
      `Its title says Kickstarter or pre-order, but a pre-sale on a run says which of this product's units are the campaign's, so it's planned like any item${r.cls ? `, and ranked with them: class ${r.cls} from its own sales (the backers' orders aren't sales)` : ""}.`,
    );
  if (r.stockPolicy === "min_max") out.push(`Min/max: when stock drops below ${fmt(r.minQty ?? 0)}, top it up to ${fmt(r.maxQty ?? 0)}.`);
  else if (r.stockPolicy === "target") out.push(`Target level ${fmt(r.maxQty ?? 0)}: when stock drops below it, top it back up.`);
  else if (r.safetyFrom === "item") out.push(`Safety stock ${fmt(Math.ceil(r.safetyStock))}, set on the item.`);
  else if (r.rate > 0) {
    const target = r.serviceFrom === "item" ? `the item's ${fmt(r.serviceLevel)}% target` : `class ${r.cls ?? "C"}, ${fmt(r.serviceLevel)}% target`;
    out.push(
      `Safety stock ${fmt(Math.ceil(r.safetyStock))} = ${r.z.toFixed(2)} (${target}) × ${fmt(r.sd)}/day variability × √(${r.leadTime} lead + ${settings.review_days} review days).`,
    );
  }
  out.push(
    `${r.make ? "Build time" : "Lead time"} ${r.leadTime} days${r.leadTimeFrom === "vendor" ? (r.make ? " (the CM's default)" : " (the vendor's default)") : r.leadTimeDefault ? " (the default: neither the vendor link nor the vendor has one)" : ""}.`,
  );
  // Vendor closures --- "+21 days: Chinese New Year (Jan 30–Feb 20)".
  if (r.delay) {
    const who = r.vendor ? r.vendor.short_name || r.vendor.name : "The vendor";
    const what = r.make ? "build time" : "lead time";
    const n = `${r.delay.days} day${r.delay.days === 1 ? "" : "s"}`;
    out.push(
      r.delay.late
        ? `${delayText(r.delay)}. ${who} is closed then, so an order placed now lands ${n} later than the ${r.leadTime}-day ${what} alone.`
        : `${delayText(r.delay)}. ${who} is closed then, so order-by is ${n} earlier than the ${r.leadTime}-day ${what} alone.`,
    );
  }
  const supply = [
    `${fmt(r.onHand)} on hand ${r.level >= 0 ? "(HHL and the CMs)" : "at HHL"}${r.available !== r.onHand ? ` (${fmt(r.available)} available)` : ""}`,
  ];
  for (const x of r.incoming)
    supply.push(
      `${fmt(x.qty)} on ${x.poNumber}${x.draft ? " (draft)" : ""} ${x.late ? "past due" : `arriving ${shortDate(x.arrive, today)}`}${x.direct ? ` (its other ${fmt(x.direct)} go straight to backers)` : ""}`,
    );
  out.push(`${supply.join("; ")}.`);
  if (r.stockoutDay !== null) out.push(r.stockoutDay === 0 ? "Out of stock now." : `Runs out ${date(r.stockoutDay)} (${fmt(r.stockoutDay)} days of cover).`);
  else if (r.coverDays !== null) out.push(`About ${fmt(r.coverDays)} days of cover (${fmt(Math.round(r.coverDays / MONTH_DAYS))} months).`);
  if (r.atRisk) out.push(`At risk: ${r.riskNote}.`);
  const floor = r.stockPolicy === "forecast" ? "safety stock" : r.stockPolicy === "min_max" ? `the min (${fmt(r.minQty ?? 0)})` : "the target";
  if (r.needDay !== null && r.orderByDay !== null) {
    out.push(
      `Falls below ${floor} ${r.needDay === 0 ? "now" : date(r.needDay)}, so order ${r.orderByDay <= 0 ? "now" : `by ${date(r.orderByDay)}`} (${r.leadTime}-day lead time${leadNote(r, date)})${r.orderByDay <= settings.review_days ? `: within the ${settings.review_days}-day review period` : ""}.`,
    );
  } else if (r.rate > 0 || r.b2bOpen > 0) out.push(`Stays above ${floor} for the next ${fmt(r.horizon)} days.`);
  // Cover through --- the person's day to buy to.
  if (r.coverTo) {
    const whose = r.coverTo.from === "vendor" ? `your day for ${r.vendor?.short_name || r.vendor?.name || "this vendor"}` : "your day for every vendor";
    out.push(
      r.coverTo.short
        ? `Covering through ${date(r.coverTo.day)} (${whose}): it falls below ${r.stockPolicy === "forecast" ? "safety stock" : "its floor"} before then, so it's due now and the order lasts to then at least.`
        : `Covering through ${date(r.coverTo.day)} (${whose}): it lasts past then without an order.`,
    );
  }
  if (r.qty > 0) {
    const land = r.arriveDay ?? Math.max(0, r.orderByDay ?? 0) + r.leadTime;
    // Vendor closures --- one that lands ahead of a closure (needed during it) covers from the day it's needed.
    const from = Math.max(land, r.needDay ?? land);
    const after = from > land ? `from ${date(from)}` : "after that";
    let s =
      r.maxQty !== null
        ? `Order ${fmt(r.qty)}: lands ${date(land)}; topping stock up to ${fmt(r.maxQty)} then takes ${fmt(r.rawQty)}`
        : r.coverTo?.short && r.coverTo.day + 1 - from > r.coverFor
          ? `Order ${fmt(r.qty)}: lands ${date(land)}; keeping safety stock ${after} through ${date(r.coverTo.day)} takes ${fmt(r.rawQty)}`
          : `Order ${fmt(r.qty)}: lands ${date(land)}; keeping safety stock for ${r.coverFor} days ${after}${r.coverFrom === "vendor" ? " (the vendor's order cycle)" : ""} (to ${date(from + r.coverFor)}) takes ${fmt(r.rawQty)}`;
    if (r.qty > r.rawQty)
      s += `, raised to ${r.moq > r.rawQty ? `the MOQ of ${fmt(r.moq)}${r.moqFrom === "vendor" ? " (the vendor's default)" : ""}` : `a multiple of ${fmt(r.multiple)}`}`;
    out.push(`${s}.`);
  }
  if (r.priceHint) {
    const h = r.priceHint;
    out.push(
      `+${fmt(h.extra)} reaches the ${fmt(h.qty)} price (${fmtMoney(h.unitPrice, r.currency)} vs ${fmtMoney(h.currentUnitPrice, r.currency)}): saves ${fmtMoney(h.saves, r.currency)}, about ${fmt(Math.round(h.extraCoverDays))} more days of stock.`,
    );
  }
  // Make planning --- where it ships, and the lead times stacked above it.
  const next = r.orders[0];
  if (next && r.qty > 0) {
    const place = (id: string | null) => (id ? plan.mrp?.names.get(id) || "the CM" : "HHL");
    if (next.shipTo && next.shipTo !== plan.mrp?.hhl && next.pegs.some((p) => p.kind === "build"))
      out.push(`Ships to ${place(next.shipTo)}, where the builds use it.`);
    if (next.chain.length) {
      const steps = next.chain.map((c) => `${plan.byItem?.get(c.itemId)?.item.sku || "a build"} ${c.lead} days`);
      out.push(
        `Cumulative lead time ${next.cumLead} days: ${ownLead(next)} days to get it, then the builds above it (${steps.join(", then ")}), so it's ordered that long before the sell item is needed at HHL.`,
      );
    }
  }
  if (r.overstock && r.rate + r.depRate > 0) out.push(`More than ${fmt(settings.overstock_months)} months of cover.`);
  else if (r.overstock) out.push(r.item.kind === "sell" ? "Has stock but no sales in 12 months." : "Has stock but no builds use it.");
  return out;
}
